import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
// Components
import ConfirmationModal from 'components/common/ConfirmationModal';
import AssignOrderModal from './AssignOrderModal';
// Helpers
import { DROP_REASONS, NOT_DONE_STATES } from 'constants/OrdersFilters';
import { hasPermission } from 'constants/Helpers';
// Selectors
import { getOpsList, getOpsNames } from 'store/orders/OrdersListSelectors';
import { getPermissionsData } from 'store/app/AppSelectors';
// Localstorage
import { getFromLocalStorage } from 'helpers/LocalStorage';
// API
import FloristAssignmentService from 'services/FloristAssignmentService';

const CAPACITY_OVERRIDE = 'CapacityOverrideRequired';
const ALREADY_REASSIGNED_MESSAGE = 'This order was already reassigned.';
// Reasons the backend gives when the order has already moved on. AlreadyReassigned is
// the reason asked for in CHAR-11656; until it ships, a 400 on a row that is no longer
// Not Done reads the same way to the florist.
const STALE_ROW_REASONS = ['AlreadyReassigned', 'AssignmentConflict'];
const NOT_DONE_ONLY = /not done order can be reassigned/i;

/**
 * True when the click lost a race: someone else took or reassigned the order first.
 */
const isRowAlreadyHandled = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (STALE_ROW_REASONS.includes(data?.error_reason)) {
    return true;
  }
  // Any other conflict means the row on screen is out of date.
  if (status === 409 && data?.error_reason !== CAPACITY_OVERRIDE) {
    return true;
  }
  return status === 400 && NOT_DONE_ONLY.test(data?.message ?? '');
};

const DroppedOrdersTable = ({ rows, isLoading, onReassigned }) => {
  const navigate = useNavigate();
  const opsList = useSelector((state) => getOpsList({ state }));
  const opsNames = useSelector((state) => getOpsNames({ state }));
  const permissions = useSelector((state) => getPermissionsData({ state }));
  // A head of florists can hand the order to anyone; everyone else can only take it.
  const isHof = hasPermission(permissions, 'florist_orders', 'head_assign');
  const mainOpsId = getFromLocalStorage('opsId');
  const mainOpsName = getFromLocalStorage('opsName') || '';
  const [busyOrderId, setBusyOrderId] = useState(null);
  const [assignOrder, setAssignOrder] = useState(null);
  const [capacityRetry, setCapacityRetry] = useState(null);

  // Rows carry only an opsId. Names come from the pairs seen on the order lists, the
  // sub/B2B OPS list, or the logged in florist's own OPS - whichever knows it.
  const getOpsName = (opsId) => {
    if (opsId == null) {
      return '-';
    }
    if (opsNames[opsId]) {
      return opsNames[opsId];
    }
    const fromList = opsList.find((ops) => `${ops.id}` === `${opsId}`)?.name;
    if (fromList) {
      return fromList;
    }
    if (`${opsId}` === `${mainOpsId}` && mainOpsName) {
      return mainOpsName;
    }
    return opsId;
  };

  const reassign = async (orderId, targetFloristId, acknowledgeCapacityOverride) => {
    setBusyOrderId(orderId);
    try {
      await FloristAssignmentService.reassignOrder(orderId, {
        targetFloristId: targetFloristId ?? null,
        acknowledgeCapacityOverride,
      });
      toast.success(targetFloristId ? 'Order reassigned' : 'Order assigned to you');
      setCapacityRetry(null);
      onReassigned();
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.error_reason === CAPACITY_OVERRIDE) {
        setCapacityRetry({ orderId, targetFloristId });
      } else if (isRowAlreadyHandled(err)) {
        // Say it in the app's own words and pull the row back in line with the server.
        toast.error(ALREADY_REASSIGNED_MESSAGE);
        setCapacityRetry(null);
        onReassigned();
      } else {
        toast.error(data?.message ?? 'Could not take this order');
      }
    } finally {
      setBusyOrderId(null);
    }
  };

  return (
    <>
      <div className="dropped-orders-table">
        <div className="dropped-row dropped-head">
          <span>Order ID</span>
          <span>Drop reason</span>
          <span>Assigned / Dropped florist</span>
          <span>OPS</span>
          <span>Dropped at</span>
          <span>Action</span>
        </div>

        {rows?.length > 0 ? (
          rows.map((row, i) => {
            const isReassigned = row?.notDoneState === NOT_DONE_STATES.REASSIGNED;
            return (
              <div className="dropped-row" key={`${row?.orderId}-${i}`}>
                <span className="dropped-order-id">
                  <button type="button" onClick={() => navigate(`/orderdetails/${row?.orderId}`)}>
                    {row?.orderId}
                  </button>
                  {/* Every row here is a dropped order - only flag the reassigned ones. */}
                  {isReassigned && <span className="dropped-state-tag reassigned">Reassigned</span>}
                </span>

                <span>{DROP_REASONS[row?.notDoneReason] ?? '-'}</span>

                <span className="dropped-florists">
                  {row?.notDoneByFloristName ?? '-'}
                  {isReassigned && row?.floristName && (
                    <>
                      <i className="dropped-arrow">→</i>
                      {row.floristName}
                    </>
                  )}
                </span>

                <span className="dropped-ops">{getOpsName(row?.opsId)}</span>

                <span>
                  {row?.notDoneAt ? moment(row.notDoneAt).format('MMM DD, hh:mm A') : '-'}
                </span>

                <span>
                  {isReassigned ? (
                    <button type="button" className="dropped-action assigned" disabled>
                      Assigned
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="dropped-action"
                      disabled={busyOrderId === row?.orderId}
                      onClick={() =>
                        isHof ? setAssignOrder(row) : reassign(row?.orderId, null, false)
                      }
                    >
                      {isHof ? 'Reassign' : 'Assign to me'}
                    </button>
                  )}
                </span>
              </div>
            );
          })
        ) : (
          <h4 className="no-orders-title">{isLoading ? '' : 'No dropped orders'}</h4>
        )}
      </div>

      {assignOrder && (
        <AssignOrderModal
          isModalOpen={Boolean(assignOrder)}
          setIsOpenModal={() => setAssignOrder(null)}
          orderData={{ id: assignOrder?.orderId, deliveryDate: assignOrder?.deliveryDate }}
          fetchData={onReassigned}
          onAssignToMe={() => reassign(assignOrder?.orderId, null, false)}
          onAssignToFlorist={(floristId) => reassign(assignOrder?.orderId, floristId, false)}
        />
      )}

      {capacityRetry && (
        <ConfirmationModal
          isOpen={Boolean(capacityRetry)}
          setIsOpen={() => setCapacityRetry(null)}
          message={
            capacityRetry.targetFloristId
              ? 'That florist is already at capacity. Reassign the order anyway?'
              : 'You are already at your order capacity. Take this order anyway?'
          }
          handleCancelClick={() => setCapacityRetry(null)}
          handleProceedClick={() =>
            reassign(capacityRetry.orderId, capacityRetry.targetFloristId, true)
          }
          proceedBtnName="Continue"
          isDisabledBtn={busyOrderId === capacityRetry.orderId}
        />
      )}
    </>
  );
};

export default DroppedOrdersTable;
